import { cn } from '@/lib/utils';

interface SparklineProps {
  /** Numeric values, oldest first. Empty array renders nothing. */
  values: number[];
  width?: number;
  height?: number;
  className?: string;
  /** Optional y-axis clamp. If omitted, uses min/max of values. */
  domain?: [number, number];
  tone?: 'auto' | 'success' | 'warn' | 'danger';
}

function toneClass(tone: NonNullable<SparklineProps['tone']>): string {
  if (tone === 'success') return 'stroke-emerald-400';
  if (tone === 'warn') return 'stroke-amber-400';
  if (tone === 'danger') return 'stroke-rose-400';
  return 'stroke-primary';
}

function fillClass(tone: NonNullable<SparklineProps['tone']>): string {
  if (tone === 'success') return 'fill-emerald-400/10';
  if (tone === 'warn') return 'fill-amber-400/10';
  if (tone === 'danger') return 'fill-rose-400/10';
  return 'fill-primary/10';
}

/**
 * Tiny inline line chart. No axes, no labels — just a stroke. Used
 * inside stat cards to show recent trend.
 */
export function Sparkline({
  values,
  width = 120,
  height = 32,
  className,
  domain,
  tone = 'auto',
}: SparklineProps) {
  if (values.length < 2) {
    return <div className={cn('text-xs text-muted-foreground', className)}>—</div>;
  }
  const [dMin, dMax] = domain ?? [Math.min(...values), Math.max(...values)];
  const range = dMax - dMin || 1;
  const stepX = width / (values.length - 1);
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - dMin) / range) * height;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const path = `M ${points.join(' L ')}`;
  const area = `${path} L ${width},${height} L 0,${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('overflow-visible', className)}
      preserveAspectRatio="none"
    >
      <path d={area} className={fillClass(tone)} />
      <path
        d={path}
        className={cn('fill-none', toneClass(tone))}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
